import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { HdChartHoverHighlightService } from '../hd-chart-hover-highlight.service';
import { HD_LANG_RECOMPUTE_ON_LOAD, HdContentLanguageService } from '../hd-content-language.service';
import { HdGateInfoService } from '../hd-gate-info.service';
import {
  HdCycleEncounterKind,
  HdCycleGateActivationVm,
  HdCycleGateSource,
} from '../hd-cycle-keynotes.builder';
import {
  HD_CYCLE_ENCOUNTER_KIND_FA_ICONS,
  HD_CYCLE_PLANET_GROUPS,
  HdCyclePlanetGroupKey,
} from '../hd-cycles-cheat-sheet-metadata';

/** Aanvraag om het gate-info-paneel te openen (zelfde shape als de chart-klik). */
export interface HdCycleGateLineInfoRequest {
  gate: number;
  origin: { x: number; y: number };
}

/** Glyph-groep: alle activaties met dezelfde planeet-groep-keynote. */
interface GateActivationGroupVm {
  readonly glyphs: readonly string[];
  /** Vertaalde groep-keynote (Richting/Mensen/Ontwikkeling); '' voor zon/aarde. */
  readonly label: string;
  /** Cheat-sheet-FA-icoon van de groep; '' voor zon/aarde. */
  readonly icon: string;
}

/**
 * Poort-chip met keynotes voor het cycle-scenario: klikbare poort-badge
 * (bron-kleur: natal-blauw / cycle-groen / beide = verloop, zoals de two-color
 * overlay op de chart) gevolgd door de planeetglyphs. Een cycle-/both-chip
 * toont daarnaast de planeet-groep-keynotes (Richting/Mensen/Ontwikkeling, met
 * hun cheat-sheet-icoon; zon/aarde glyph-only) en de Tegenkoming/Vernieuwing-
 * keynote (icoon + term, uitleg als tooltip); een natal-chip blijft chip+glyph.
 * Gebruikt in de kanaal-items én voor de losse Omgevend-poorten.
 */
@Component({
  selector: 'hd-cycle-gate-keynote-line',
  templateUrl: './hd-cycle-gate-keynote-line.component.html',
  styleUrl: './hd-cycle-gate-keynote-line.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  host: {
    '[class.is-natal]': "source() === 'natal'",
  },
})
export class HdCycleGateKeynoteLineComponent {
  private readonly contentLang = inject(HdContentLanguageService);
  private readonly gateInfo = inject(HdGateInfoService);
  private readonly hoverHighlight = inject(HdChartHoverHighlightService, { optional: true });

  readonly gate = input.required<number>();
  /** Bron van de poort; kleurt de badge/glyphs en bepaalt of er keynotes staan. */
  readonly source = input<HdCycleGateSource>('cycle');
  readonly activations = input.required<readonly HdCycleGateActivationVm[]>();
  /**
   * Glyphs van de andere bron op een both-poort (composite: beide personen
   * activeren de poort) — gerenderd in natal-blauw vóór de gewone glyphs.
   */
  readonly secondaryActivations = input<readonly HdCycleGateActivationVm[]>([]);
  readonly encounter = input<HdCycleEncounterKind | null>(null);

  readonly gateInfoRequested = output<HdCycleGateLineInfoRequest>();

  private readonly lang = computed(() => {
    this.contentLang.translationsLoadedTick();
    return this.contentLang.effectiveLang();
  }, HD_LANG_RECOMPUTE_ON_LOAD);

  /** Keynotes horen alleen bij cycle-activaties — een natal-chip blijft kaal. */
  protected readonly showKeynotes = computed(() => this.source() !== 'natal');

  /** Tooltip op de poort-badge: de twee poort-keynotes uit de gates-scope. */
  protected readonly gateTooltip = computed(() => {
    const lang = this.lang();
    const keynotes = [
      this.gateKeynote('keynote1', lang),
      this.gateKeynote('keynote2', lang),
    ].filter(value => value !== '');
    return keynotes.length > 0 ? keynotes.join(' · ') : null;
  });

  /**
   * Activaties gegroepeerd per planeet-groep-keynote, in volgorde van
   * verschijnen; zon/aarde vormen samen de label-loze groep.
   */
  protected readonly groups = computed<readonly GateActivationGroupVm[]>(() => {
    const lang = this.lang();
    const groups: { key: HdCyclePlanetGroupKey | null; glyphs: string[] }[] = [];
    for (const activation of this.activations()) {
      const existing = groups.find(group => group.key === activation.planetGroup);
      if (existing) {
        existing.glyphs.push(activation.glyph);
      } else {
        groups.push({ key: activation.planetGroup, glyphs: [activation.glyph] });
      }
    }
    const showKeynotes = this.showKeynotes();
    return groups.map(group => ({
      glyphs: group.glyphs,
      label: group.key != null && showKeynotes ? this.cyclesText(`planetGroups.${group.key}.title`, lang) : '',
      icon: group.key != null && showKeynotes
        ? HD_CYCLE_PLANET_GROUPS.find(candidate => candidate.key === group.key)?.faIcon ?? ''
        : '',
    }));
  });

  protected readonly encounterTerm = computed(() => {
    const encounter = this.encounter();
    if (encounter == null || !this.showKeynotes()) {
      return '';
    }
    return this.cyclesText(`encounterRenewal.terms.${encounter}`, this.lang());
  });

  protected readonly encounterIcon = computed(() => {
    const encounter = this.encounter();
    return encounter == null ? '' : HD_CYCLE_ENCOUNTER_KIND_FA_ICONS[encounter];
  });

  protected readonly encounterTooltip = computed(() => {
    const encounter = this.encounter();
    return encounter == null ? '' : this.cyclesText(`encounterRenewal.${encounter}`, this.lang());
  });

  protected readonly secondaryGlyphs = computed(() =>
    this.secondaryActivations().map(activation => activation.glyph));

  protected onGateClick(event: MouseEvent): void {
    this.gateInfoRequested.emit({ gate: this.gate(), origin: { x: event.clientX, y: event.clientY } });
  }

  /** Hover op het poortnummer → highlight de poort in de chart (lijn-vergelijk). */
  protected onGateEnter(): void {
    this.hoverHighlight?.setGateHover(this.gate(), 'chart-gate');
  }

  protected onGateLeave(): void {
    this.hoverHighlight?.setGateHover(null, 'chart-gate');
  }

  /** Tekst uit de cycles-scope met NL-fallback voor (nog) lege EN-velden. */
  private cyclesText(key: string, lang: string): string {
    const value = this.gateInfo.cyclesCheatSheetTextIn(key, lang);
    if (value !== '' || lang === 'nl') {
      return value;
    }
    return this.gateInfo.cyclesCheatSheetTextIn(key, 'nl');
  }

  /** Poort-keynote uit de gates-scope, met dezelfde NL-fallback. */
  private gateKeynote(field: 'keynote1' | 'keynote2', lang: string): string {
    const value = this.gateInfo.gateInfoIn(this.gate(), lang).translations[field];
    if (value !== '' || lang === 'nl') {
      return value;
    }
    return this.gateInfo.gateInfoIn(this.gate(), 'nl').translations[field];
  }
}
